import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';

// Shared UI Library Components
import { INotificationItem } from '@ppmcore/seven-ppm-core-shared-components-react';

// Store actions & selectors
import { setSocketLastMessage } from '../store/socket/socket.thunks';
import { openNotification } from '../store/app-notifications/app-notifications.thunks';
import { updateCurrentCurrencyFromSocket } from '../store/currency/currency.thunks';
import { getSocketToken } from '../store/socket/socket.selectors';
import { updateStatusCounsellorByRates } from '../store/current-counsellor/current-counsellor.thunks';

// Socket entity
import { EWebSocketEvent, EWebSocketPackageType } from '../enums/web-socket.enums';
import { IWebSocketDataEvent } from '../interfaces/web-socket.interfaces';
import { IMessageItem } from '../interfaces/chat.interfaces';
import { ICurrency } from '../interfaces/company.interfaces';
import { ICounsellor } from '../interfaces/counsellors.interfaces';

/**
 * Custom React Hook for adding a WebSockets connection.
 */
export const useWebSockets = () => {
  const timerIds: { pong: NodeJS.Timer | null, ping: NodeJS.Timer | null } = {
    pong: null,
    ping: null
  };

  const socketToken = useSelector(getSocketToken);

  const dispatch = useDispatch<any>();

  const socket = useMemo(() => {
    if (!socketToken || !process.env.REACT_APP_SOCKET_URL) return null;
    return io(process.env.REACT_APP_SOCKET_URL, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: {
        channel: socketToken,
        app: 'web_admin',
        token: localStorage.getItem('token')
      }
    });
  }, [socketToken]);

  /**
   * Clear Pong Timer.
   */
  const clearPongTimer = (): void => {
    if (!timerIds.pong) return;
    clearTimeout(timerIds.pong);
    timerIds.pong = null;
  }

  /**
   * Clear Ping Timer.
   */
  const clearPingTimer = (): void => {
    if (!timerIds.ping) return;
    clearInterval(timerIds.ping);
    timerIds.ping = null;
  }

  /**
   * Starts the pong waiting timer.
   */
  const startPongWaitingTimer = (): void => {
    clearPongTimer();
    timerIds.pong = setTimeout(() => {
      reconnectSockets();
    }, 15000);
  }

  /**
   * Initializes the ping event to keep the WebSocket connection alive.
   */
  const initPingEvent = (): void => {
    clearPingTimer();
    timerIds.ping = setInterval(() => {
      socket?.emit(EWebSocketEvent.Ping);
      startPongWaitingTimer();
    }, 20000);
  }

  /**
   * Handles the 'Connect' event of the web socket.
   */
  const onConnectEvent = (): void => {}

  /**
   * Handles paid-message type event.
   * @param {EWebSocketPackageType} type - The type from the web socket by user channel.
   * @param {IMessageItem} data - The message data  the web socket by user channel.
   */
  const onPaidMessageEvent = ({ data }: { data: IMessageItem }): void => {
    dispatch(openNotification({
      type: 'info',
      description: `You have new message from ${ data.worker?.first_name + ' ' + data.worker?.last_name }`
    }));
  }

  /**
   * Handles message replied type event.
   * @param {EWebSocketPackageType} type - The type from the web socket by user channel.
   * @param {INotificationItem} data - The notification data  the web socket by user channel.
   */
  const onMessageRepliedEvent = ({ data }: { data: INotificationItem }): void => {
    // dispatch(addNotificationFromSocket(data));

    dispatch(openNotification({
      type: 'info',
      description: `You have new replied from ${ data.worker?.first_name + ' ' + data.worker?.last_name }`
    }));
  }

  /**
   * Handles company-currency-updated type event.
   * @param {EWebSocketPackageType} type - The type from the web socket by user channel.
   * @param Object data - The notification data  the web socket by user channel.
   */
  const onCompanyCurrencyUpdatedEvent = ({ data }: { data: { currency: ICurrency } }): void => {
    dispatch(updateCurrentCurrencyFromSocket(data.currency));
  }

  /**
   * Handles deactivated-by-rates type event.
   * @param {EWebSocketPackageType} type - The type from the web socket by user channel.
   * @param Object data - The counsellor data  the web socket by user channel.
   */
  const onDeactivatedByRatesEvent = ({ data }: { data: ICounsellor }): void => {
    dispatch(updateStatusCounsellorByRates(data));
  }


  /**
   * Handles worker-signup-finished type event.
   * @param {EWebSocketPackageType} type - The type from the web socket by user channel.
   * @param Object data - The counsellor data  the web socket by user channel.
   */
  const onWorkerSignupFinishedEvent = ({ data }: { data: ICounsellor }): void => {
    dispatch(updateStatusCounsellorByRates(data));
  }

  /**
   * Handles incoming data events from the web socket.
   * @param {IWebSocketDataEvent} dataEvent - The data received from the web socket by user channel.
   */
  const onDataEvent = (dataEvent: IWebSocketDataEvent<any>): void => {
    if (!Object.keys(dataEvent).length) {
      return;
    }
    dispatch(setSocketLastMessage(dataEvent));

    if (dataEvent.type === EWebSocketPackageType.PaidMessage) {
      onPaidMessageEvent({ data: dataEvent.data });
    }

    if (dataEvent.type === EWebSocketPackageType.MessageReplied) {
      onMessageRepliedEvent({ data: dataEvent.data });
    }

    if (dataEvent.type === EWebSocketPackageType.DeactivatedByRates) {
      onDeactivatedByRatesEvent({ data: dataEvent.data });
    }

    if (dataEvent.type === EWebSocketPackageType.CompanyCurrencyUpdated) {
      onCompanyCurrencyUpdatedEvent({ data: dataEvent.data });
    }

    if (dataEvent.type === EWebSocketPackageType.WorkerSignupFinished) {
      onWorkerSignupFinishedEvent({ data: dataEvent.data });
    }
  }

  /**
   * Handles connect to room event from the web socket.
   * @param {string} dataEvent - The message from the web socket by user channel.
   */
  const onConnectToRoomEvent = ({ type, data }: { type: EWebSocketPackageType, data: { message: string } }): void => {}

  /**
   * Handles the 'Pong' event of the web socket.
   */
  const onPongEvent = () => {
    clearPongTimer();
  };

  /**
   * Unsubscribes from events of the web socket.
   */
  const unsubscribeFromEvents = () => {
    socket?.off(EWebSocketEvent.Connect, onConnectEvent);
    socket?.off(EWebSocketEvent.ConnectToRoom, onConnectToRoomEvent);
    socket?.off(EWebSocketEvent.Pong, onPongEvent);
    socket?.off(`${ process.env.REACT_APP_SOCKET_CHANNEL_PREFIX }${ socketToken }`, onDataEvent);
  };

  /**
   * Subscribes to events of the web socket.
   */
  const subscribeForEvents = () => {
    socket?.on(EWebSocketEvent.Connect, onConnectEvent);
    socket?.on(EWebSocketEvent.ConnectToRoom, onConnectToRoomEvent);
    socket?.on(EWebSocketEvent.Pong, onPongEvent);
    socket?.on(`${ process.env.REACT_APP_SOCKET_CHANNEL_PREFIX }${ socketToken }`, onDataEvent);
  };

  const disconnectSockets = () => {
    socket?.disconnect();
    clearPongTimer();
    clearPingTimer();
    unsubscribeFromEvents();
  }

  const reconnectSockets = () => {
    disconnectSockets();
    connectSockets();
  }

  const connectSockets = () => {
    if (!socket) return;
    socket?.connect();
    subscribeForEvents();
    initPingEvent();
  }

  useEffect(() => {
    connectSockets();
  }, [socket]);

  useEffect(() => {
    /**
     Cleaning function to destroy socket connection when component is unmounted.
     @function
     */
    return () => {
      disconnectSockets();
    };
  }, []);
};
